import React from "react";
import {
  Divider,
  Chip,
  makeStyles,
  Typography,
  Box,
  Button,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import Pagination from "@material-ui/lab/Pagination";
import UtilService from "../../helper/service";
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    borderRadius: theme.spacing(2),
    overflow: "hidden",
    boxShadow: theme.shadows[3],
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(4),
  },
  tableHeader: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tableRow: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: "column",
      alignItems: "flex-start",
    },
  },
  preferMatch: {
    backgroundColor: `${theme.palette.primary.light}15 !important`,
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    position: "relative",
    "&:before": {
      content: '"Recommended"',
      position: "absolute",
      top: theme.spacing(1),
      right: theme.spacing(1),
      fontSize: "0.7rem",
      fontWeight: 600,
      color: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.light}40`,
      padding: "2px 8px",
      borderRadius: theme.spacing(0.5),
    },
  },
  dateColumn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 100,
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    marginRight: theme.spacing(2),
  },
  dayText: {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
    textTransform: "uppercase",
  },
  dateText: {
    fontWeight: 700,
    fontSize: "1.5rem",
    color: theme.palette.primary.main,
    lineHeight: 1,
  },
  monthText: {
    fontWeight: 500,
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
  },
  timeContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 120,
    padding: theme.spacing(1),
    marginRight: theme.spacing(2),
  },
  timeText: {
    fontWeight: 600,
    fontSize: "1rem",
    color: theme.palette.text.primary,
  },
  timeDivider: {
    width: "100%",
    margin: theme.spacing(0.5, 0),
  },
  specialityContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    minWidth: 150,
    marginRight: theme.spacing(2),
  },
  specialityChip: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 500,
    fontSize: "0.75rem",
  },
  hospitalContainer: {
    flex: 1,
    marginRight: theme.spacing(2),
  },
  hospitalName: {
    fontWeight: 600,
    fontSize: "1rem",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
    display: "flex",
    alignItems: "center",
    "& svg": {
      marginRight: theme.spacing(0.5),
      fontSize: "1rem",
    },
  },
  wardInfo: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    "& span": {
      fontWeight: 500,
      marginRight: theme.spacing(0.5),
    },
  },
  paymentContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 150,
    marginRight: theme.spacing(2),
  },
  paymentText: {
    fontWeight: 700,
    fontSize: "1rem",
    color: theme.palette.success.main,
    display: "flex",
    alignItems: "center",
    "& svg": {
      marginRight: theme.spacing(0.5),
    },
  },
  actionContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 100,
  },
  detailButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0.5, 2),
  },
  monthHeader: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1.5, 2),
    marginTop: theme.spacing(3),
    borderTopLeftRadius: theme.spacing(1),
    borderTopRightRadius: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    "& svg": {
      marginRight: theme.spacing(1),
      color: theme.palette.primary.main,
    },
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3, 0),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.background.paper,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      display: "none",
    },
  },
  headerCell: {
    padding: theme.spacing(0, 1),
    "&:nth-child(1)": { minWidth: 100, marginRight: theme.spacing(2) },
    "&:nth-child(2)": { minWidth: 120, marginRight: theme.spacing(2) },
    "&:nth-child(3)": { minWidth: 150, marginRight: theme.spacing(2) },
    "&:nth-child(4)": { flex: 1, marginRight: theme.spacing(2) },
    "&:nth-child(5)": { minWidth: 150, marginRight: theme.spacing(2) },
    "&:nth-child(6)": { minWidth: 100 },
  },
}));

const ShiftTable = (props) => {
  const { shiftList, handleChangePage, page } = props;
  const classes = useStyles();

  const shiftData = shiftList?.data?.data.reduce((shiftFor, getData) => {
    let date2 = new Date(getData.date);
    const fullyear = date2.getFullYear();
    const getMon = date2.toLocaleString("en", { month: "short" });
    const monthYear = `${fullyear} ${getMon}`;
    if (!shiftFor[monthYear]) shiftFor[monthYear] = [];
    shiftFor[monthYear].push(getData);
    return shiftFor;
  }, {});

  return (
    <div className={classes.tableContainer}>
      {/* Table Header */}
      <div className={classes.headerRow}>
        <div className={classes.headerCell}>Date</div>
        <div className={classes.headerCell}>Shift Time</div>
        <div className={classes.headerCell}>Speciality</div>
        <div className={classes.headerCell}>Site</div>
        <div className={classes.headerCell}>Payment</div>
        <div className={classes.headerCell}>Action</div>
      </div>

      {/* Table Body */}
      <div className={classes.tableBody}>
        {shiftData && Object.keys(shiftData).length > 0  ? (
          Object.entries(shiftData).map(([monthYear, shifts], index) => {
            let dateReverse = monthYear.split(" ").reverse().join(" ");
            return (
              <React.Fragment key={index}>
                {/* Month Header */}
                <Box className={classes.monthHeader}>
                  <CalendarTodayIcon />
                  <Typography variant="h6">{dateReverse}</Typography>
                </Box>

                {/* Shifts for this month */}
                {shifts.map((result, index1) => {
                  let specilaity_list =
                    result.speciality_name != null
                      ? result.speciality_name.split(",")
                      : [];
                  let date2 = new Date(result.date);
                  let dayNum = date2.toLocaleString("en", { day: "2-digit" });
                  let getMon = date2.toLocaleString("en", { month: "short" });
                  const dayName = UtilService.getDayName(date2);

                  return (
                    <div
                      className={result.preference_match == 1 ? `${classes.preferMatch} ${classes.tableRow}` : classes.tableRow}
                      key={index1}
                    >
                      {/* Date Column */}
                      <Box className={classes.dateColumn}>
                        <Typography className={classes.dayText}>{dayName}</Typography>
                        <Typography className={classes.dateText}>{dayNum}</Typography>
                        <Typography className={classes.monthText}>{getMon}</Typography>
                      </Box>

                      {/* Time Column */}
                      <Box className={classes.timeContainer}>
                        <AccessTimeIcon color="action" />
                        <Typography className={classes.timeText}>
                          {result.start_time ? result.start_time.slice(0, 5) : ""}
                        </Typography>
                        <Divider className={classes.timeDivider} />
                        <Typography className={classes.timeText}>
                          {result.end_time ? result.end_time.slice(0, 5) : ""}
                        </Typography>
                      </Box>

                      {/* Speciality Column */}
                      <Box className={classes.specialityContainer}>
                        {specilaity_list.map((list, index) => (
                          <Chip
                            className={classes.specialityChip}
                            label={list}
                            key={index}
                            size="small"
                          />
                        ))}
                      </Box>

                      {/* Hospital Column */}
                      <Box className={classes.hospitalContainer}>
                        <Typography className={classes.hospitalName}>
                          <LocationOnIcon />
                          {result.hospital_name}
                        </Typography>
                        <Box className={classes.wardInfo}>
                          <Typography>
                            <span>Ward:</span>
                            {result.ward_name}
                          </Typography>
                          <Typography>
                            <span>Ward Type:</span>
                            {result.ward_type}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Payment Column */}
                      <Box className={classes.paymentContainer}>
                        <Typography className={classes.paymentText}>
                          £{Number(result.candidate_payable > 0 ? result.candidate_payable : result.payable)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">Payable Amount</Typography>
                      </Box>

                      {/* Action Column */}
                      <Box className={classes.actionContainer}>
                        {result?.booking_record_perm_for_signees &&
                          result?.booking_record_perm_for_signees
                            ?.review_shifts && (
                            <Button
                              component={Link}
                              to={`shifts/${result.id}`}
                              className={classes.detailButton}
                              color="primary"
                              variant="contained"
                              size="small"
                              startIcon={<EventAvailableIcon />}
                            >
                              Details
                            </Button>
                          )}
                      </Box>
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })
        ) : (
          <Box className={classes.emptyState}>
            <Typography variant="h6" color="textSecondary">
              No shifts available
            </Typography>
            <Typography variant="body2">
              Check back later for new shift opportunities
            </Typography>
          </Box>
        )}
      </div>

      {/* Pagination */}
      {shiftList?.data?.last_page > 1 && (
        <Box className={classes.paginationContainer}>
          <Pagination
            onChange={handleChangePage}
            page={page}
            count={shiftList?.data?.last_page}
            boundaryCount={2}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
    </div>
  );
};

export default ShiftTable;